import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType as NewEventType } from "@/features/events/types/EventType"
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Appointment } from "../../../features/appointments/types/Appointment"
import { saveEvent } from "../../../utils/apis/user-service"
import { ControlPanelElement, FocusType } from "../../../utils/enums/ControlPanel"
import { EventType } from "../../../utils/enums/EventType"
import { BloodTestTrackingStatus } from "../../../utils/enums/TrackingStatus"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useConsultsStore } from "../../../utils/stores/useConsultsStore"
import { useExpertsStore } from "../../../utils/stores/useExpertsStore"
import { usePlansStore } from "../../../utils/stores/usePlansStore"
import { useSurveyDataStore } from "../../../utils/stores/useSurveyDataStore"
import { useTrackersStore } from "../../../utils/stores/useTrackersStore"
import { useUserStore } from "../../../utils/stores/useUserStore"

const ControlPanel = () => {
    const navigate = useNavigate()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const { userData } = useUserStore()
    const email = userData.email
    // const { dnaTestTracker } = useDNATestTracker()
    // const status = dnaTestTracker.status
    const { surveyData } = useSurveyDataStore()
    const healthAssessmentCompleted = surveyData.healthAssessmentCompleted
    const { consultsData } = useConsultsStore()
    const { experts } = useExpertsStore()
    const nutritionist = experts.nutritionist
    const nutriConsultsLeft = consultsData.nutritionistConsultsLeft
    const nutriFutureAppointments = consultsData.nutritionistFutureConsults
    const specialistFutureAppointments = consultsData.specialistFutureConsults
    // const { bloodTestTracker } = useBloodTestTracker()
    // const bloodStatus = bloodTestTracker.bloodStatus
    const { trackers } = useTrackersStore()
    const dnaStatus = trackers.dnaStatus
    const bloodStatus = trackers.bloodStatus
    const { plans } = usePlansStore()
    const healthPlan = plans.healthPlans
    const { sendEvent } = useCreateEvent()

    const [isExpanded, setIsExpanded] = useState(!healthPlan)

    const handleDnaTestNavigation = (status: DnaTestTrackingStatus | null) => {
        switch (status) {
            case null:
            case DnaTestTrackingStatus.Purchased:
            case DnaTestTrackingStatus.Departed:
            case DnaTestTrackingStatus.DeliveredToLaboratory:
            case DnaTestTrackingStatus.StartingToExtract:
            case DnaTestTrackingStatus.StartingAnalysis:
            case DnaTestTrackingStatus.ReportGenerated:
                return navigate("/dna-test")
            case DnaTestTrackingStatus.Delivered:
                return navigate("/dna-test/tutorial")
            case DnaTestTrackingStatus.TestDone:
                return navigate("/dna-test/register-swab-code")
            case DnaTestTrackingStatus.SampleRegistered:
                return navigate("/dna-test/packing")
            case DnaTestTrackingStatus.PickupRequested:
                return navigate("/dna-test/request-pickup")
            default:
                return ""
        }
    }

    const handleBloodTestNavigation = (status: BloodTestTrackingStatus | null) => {
        switch (status) {
            case null:
            case BloodTestTrackingStatus.None:
            case BloodTestTrackingStatus.Purchased:
            case BloodTestTrackingStatus.DeliveredToLab:
            case BloodTestTrackingStatus.ReportGenerated:
                return navigate("/diagnostica/tests-and-reports/blood-test")
            case BloodTestTrackingStatus.Delivered:
                return navigate("/diagnostica/tests-and-reports/blood-test/tutorial")
            case BloodTestTrackingStatus.TestDone:
                return navigate("/diagnostica/tests-and-reports/blood-test/sample-register")
            case BloodTestTrackingStatus.SampleRegistered:
                return navigate("/diagnostica/tests-and-reports/blood-test/packing")
            case BloodTestTrackingStatus.PickupRequested:
                return navigate("/diagnostica/tests-and-reports/blood-test/request-pickup")
        }
    }

    const handleHealthAssessmentClick = async () => {
        if (healthAssessmentCompleted === null) return
        handleHealthAssessmentNavigation(healthAssessmentCompleted)
        email && saveEvent(
            email,
            EventType.HealthAssessmentControlPanelItemClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "home_page",
        )

        await sendEvent({
            event: NewEventType.CLICK,
            sub_category: "control_panel_item",
            event_metadata: { item: "health_assessment"}
        })
    }

    const handleDnaTestClick = async () => {
        handleDnaTestNavigation(dnaStatus)
        email && saveEvent(
            email,
            EventType.DnaTestControlPanelItemClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "home_page",
        )

        await sendEvent({
            event: NewEventType.CLICK,
            sub_category: "control_panel_item",
            event_metadata: { item: "dna_test"}
        })
    }

    const handleBloodTestClick = async () => {
        handleBloodTestNavigation(bloodStatus)
        email && saveEvent(
            email,
            EventType.BloodTestControlPanelItemClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "home_page",
        )

        await sendEvent({
            event: NewEventType.CLICK,
            sub_category: "control_panel_item",
            event_metadata: { item: "blood_test"}
        })
    }

    const handleExpertConsultClick = async () => {
        handleSpecialistsNavigation()
        email && saveEvent(
            email,
            EventType.ExpertConsultControlPanelItemClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "home_page",
        )

        await sendEvent({
            event: NewEventType.CLICK,
            sub_category: "control_panel_item",
            event_metadata: { item: "specialist_consults"}
        })
    }

    const handleNutritionistConsultClick = async() => {
        handleNutritionistsNavigation()
        email && saveEvent(
            email,
            EventType.NutritionistConsultControlPanelItemClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "home_page",
        )

        await sendEvent({
            event: NewEventType.CLICK,
            sub_category: "control_panel_item",
            event_metadata: { item: "nutritionist_consults"}
        })
    }

    const handleHealthAssessmentNavigation = (healthAssessmentCompleted: boolean) => {
        console.log("hjh")
        switch (healthAssessmentCompleted) {
            case false:
                return navigate("/health-assessment-home-page")
            case true:
                return navigate("/check-up-assessment-home-page")
        }
    }

    const handleNutritionistsNavigation = () => {
        if (nutriFutureAppointments === null) {
            if (nutriConsultsLeft === 0) {
                if (nutritionist) {
                    navigate("/equipe/your-experts ")
                } else {
                    window.open("https://www.holifya.com/pages/consulto ", "_blank")
                }
            } else {
                navigate("/equipe/your-experts")
            }
        } else {
            navigate("/equipe")
        }
    }

    const handleSpecialistsNavigation = () => {
        if (specialistFutureAppointments === null) {
            navigate("/equipe/your-experts")
        } else {
            navigate("/equipe")
        }
    }

    // ********************************************************************************

    return (
        <div className="w-full flex flex-col rounded-[15px] shadow-platform flex-shrink-0 bg-white p-[20px] space-y-[20px]">
            <div className="w-full flex justify-between items-center">
                <div className="font-bold text-[12px] leading-[17px]">
                    Pannello di controllo
                </div>
                <div
                    className="font-bold text-[12px] leading-[14px] text-holifya-blue cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? "Mostra di meno" : "Mostra di più"}
                </div>
            </div>
            {
                isExpanded && <div className="w-full flex flex-col space-y-[10px]">
                    <ControlPanelCard
                        focusType={FocusType.None}
                        element={ControlPanelElement.HealthAssessment}
                        onClick={handleHealthAssessmentClick}
                    />
                    <ControlPanelCard
                        focusType={dnaStatus === DnaTestTrackingStatus.ReportGenerated ? FocusType.Highlight : (dnaStatus !== null ? FocusType.Classic : FocusType.None)}
                        element={ControlPanelElement.DNATestComplete}
                        onClick={handleDnaTestClick}
                    />
                    <ControlPanelCard
                        focusType={bloodStatus === BloodTestTrackingStatus.ReportGenerated ? FocusType.Highlight : ((bloodStatus === BloodTestTrackingStatus.None || bloodStatus === null || bloodStatus === undefined) ? FocusType.None : FocusType.Classic)}
                        element={ControlPanelElement.BloodExams}
                        onClick={handleBloodTestClick}
                    />
                    <ControlPanelCard
                        focusType={FocusType.None}
                        element={ControlPanelElement.ExpertConsult}
                        onClick={handleExpertConsultClick}
                        secondTextColor={specialistFutureAppointments !== null ? (specialistFutureAppointments[0].remainingHours < 24 ? "holifya-red" : "holifya-blue") : "holifya-blue"}
                    />
                    <ControlPanelCard
                        focusType={FocusType.None}
                        element={ControlPanelElement.NutritionistConsult}
                        onClick={handleNutritionistConsultClick}
                        secondTextColor={nutriFutureAppointments !== null ? (nutriFutureAppointments[0].remainingHours < 24 ? "holifya-red" : "holifya-blue") : "holifya-blue"}
                    />
                </div>
            }
        </div>
    );
}

export default ControlPanel

interface CardProps {
    focusType: FocusType,
    element: ControlPanelElement,
    onClick: () => void,
    secondTextColor?: string,
    pointer?: boolean,
}

const ControlPanelCard: React.FC<CardProps> = ({
    focusType,
    element,
    onClick,
    secondTextColor = "holifya-blue",
    pointer = true,
}) => {
    // const { dnaTestTracker } = useDNATestTracker()
    const { surveyData } = useSurveyDataStore()
    const healthAssessmentCompleted = surveyData.healthAssessmentCompleted
    const { consultsData } = useConsultsStore()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft
    const specialistFutureConsults = consultsData.specialistFutureConsults
    const nutritionistFutureConsults = consultsData.nutritionistFutureConsults
    // const { bloodTestTracker } = useBloodTestTracker()
    // const bloodStatus = bloodTestTracker.bloodStatus
    const { trackers } = useTrackersStore()
    const dnaStatus = trackers.dnaStatus
    const bloodStatus = trackers.bloodStatus

    const handleFocus = () => {
        switch (focusType) {
            case FocusType.None:
                return "border border-solid border-black"
            case FocusType.Classic:
                return "border-2 border-solid border-holifya-blue"
            case FocusType.Highlight:
                return "border-2 border-solid border-holifya-green"
        }
    }

    // ********************************************************************************

    return (
        <div
            className={`${handleFocus()} w-full h-[60px] rounded-[5px] flex items-center justify-center ${pointer && "cursor-pointer"}`}
            onClick={onClick}
        >
            <div className="w-[95%] flex items-center justify-between">
                <div className="font-medium text-[15px] leading-[21px]">{handleControlPanelElementText(element, bloodStatus !== null)}</div>
                <div className={`font-bold text-[10px] leading-[14px] text-${secondTextColor}`}>{handleControlPanelElementTextTwo(element, dnaStatus!, healthAssessmentCompleted!, specialistConsultsLeft!, nutritionistConsultsLeft!, specialistFutureConsults, nutritionistFutureConsults, bloodStatus)}</div>
            </div>
        </div>
    )
}

const handleControlPanelElementText = (element: ControlPanelElement, complete: boolean = true) => {
    switch (element) {
        case ControlPanelElement.HealthAssessment:
            return `${"\u{1F4DD}"} Health Assessment`
        case ControlPanelElement.DNATestComplete:
            return `${"\u{1F9EC}"} DNA Test Complete`
        case ControlPanelElement.BloodExams:
            return `${"\u{1FA78}"} Analisi ${complete ? "degli" : ""} Aminoacidi`
        case ControlPanelElement.ExpertConsult:
            return `👩‍⚕️ Consulto con Medico`
        case ControlPanelElement.NutritionistConsult:
            return `👨‍⚕️ Consulto con Nutrizionista`
    }
}

const handleControlPanelElementTextTwo = (element: ControlPanelElement, trackerStatus: DnaTestTrackingStatus, healthAssessmentCompleted: boolean, specialistConsultsLeft: number, nutritionistConsultsLeft: number, specialistFutureConsults: Appointment[] | null, nutritionistFutureConsults: Appointment[] | null, bloodTestStatus: BloodTestTrackingStatus | null) => {
    switch (element) {
        case ControlPanelElement.HealthAssessment:
            return handleAssessmentCardTextTwo(healthAssessmentCompleted)
        case ControlPanelElement.DNATestComplete:
            return handleTestCardTextTwo(trackerStatus)
        case ControlPanelElement.BloodExams:
            return handleBloodCardTextTwo(bloodTestStatus)
        case ControlPanelElement.ExpertConsult:
            return handleSpecialistCardTextTwo(specialistConsultsLeft, specialistFutureConsults)
        case ControlPanelElement.NutritionistConsult:
            return handleNutritionistCardTextTwo(nutritionistConsultsLeft, nutritionistFutureConsults)
    }
}

const handleSpecialistCardTextTwo = (specialistConsultsLeft: number, specialistFutureConsults: Appointment[] | null) => {
    if (specialistFutureConsults !== null) {
        const nextSpecialistConsult = specialistFutureConsults[0]
        const remainingDays = nextSpecialistConsult.remainingDays
        const remainingHours = nextSpecialistConsult.remainingHours
        const remainingMinutes = nextSpecialistConsult.remainingMinutes
        if (remainingDays >= 1) {
            return `${remainingDays === 1 ? "Manca" : "Mancano"} ${remainingDays} ${remainingDays === 1 ? "giorno" : "giorni"}`
        } else if (remainingHours > 1) {
            return `Mancano ${remainingHours} ore`
        } else if (remainingHours === 1 && remainingMinutes > 55) {
            return `Manca 1 ora`
        } else if (remainingMinutes > 1) {
            return `Mancano ${remainingMinutes} minuti`
        } else if (remainingMinutes === 1) {
            return `Manca 1 minuto`
        } else {
            return `In corso`
        }
    } else if (specialistConsultsLeft === 0 || specialistConsultsLeft === null) {
        return "Acquista"
    } else {
        return `${specialistConsultsLeft} ${specialistConsultsLeft === 1 ? "disponibile" : "disponibili"}`
    }
}

const handleNutritionistCardTextTwo = (nutritionistConsultsLeft: number, nutritionistFutureConsults: Appointment[] | null) => {
    if (nutritionistFutureConsults !== null) {
        const nextNutriConsult = nutritionistFutureConsults[0]
        const remainingDays = nextNutriConsult.remainingDays
        const remainingHours = nextNutriConsult.remainingHours
        const remainingMinutes = nextNutriConsult.remainingMinutes
        if (remainingDays >= 1) {
            return `${remainingDays === 1 ? "Manca" : "Mancano"} ${remainingDays} ${remainingDays === 1 ? "giorno" : "giorni"}`
        } else if (remainingHours > 1) {
            return `Mancano ${remainingHours} ore`
        } else if (remainingHours === 1 && remainingMinutes > 55) {
            return `Manca 1 ora`
        } else if (remainingMinutes > 1) {
            return `Mancano ${remainingMinutes} minuti`
        } else if (remainingMinutes === 1) {
            return `Manca 1 minuto`
        } else {
            return `In corso`
        }
    } else if (nutritionistConsultsLeft === 0 || nutritionistConsultsLeft === null) {
        return "Acquista"
    } else {
        return `${nutritionistConsultsLeft} ${nutritionistConsultsLeft === 1 ? "disponibile" : "disponibili"}`
    }
}

const handleAssessmentCardTextTwo = (healthAssessmentCompleted: boolean) => {
    switch (healthAssessmentCompleted) {
        case false:
            return "Rispondi al questionario"
        case true:
            return "Rispondi al check-up"
    }
}

const handleTestCardTextTwo = (trackerStatus: DnaTestTrackingStatus) => {
    switch (trackerStatus) {
        case DnaTestTrackingStatus.Purchased:
            return "In arrivo da te!"
        case DnaTestTrackingStatus.Delivered:
            return "Effettua il test"
        case DnaTestTrackingStatus.TestDone:
            return "Registra campione"
        case DnaTestTrackingStatus.SampleRegistered:
            return "Richiedi il ritiro"
        case DnaTestTrackingStatus.PickupRequested:
            return "Modifica il ritiro"
        case DnaTestTrackingStatus.Departed:
            return "Verso il laboratorio"
        case DnaTestTrackingStatus.DeliveredToLaboratory:
            return "In laboratorio"
        case DnaTestTrackingStatus.StartingToExtract:
            return "Inizio estrazione"
        case DnaTestTrackingStatus.StartingAnalysis:
            return "In analisi"
        case DnaTestTrackingStatus.AnalysisDone:
            return "Analisi completa"
        case DnaTestTrackingStatus.ReportGenerated:
            return "Visualizza risultati"
        default:
            return "Acquista DNA Test"
    }
}

const handleBloodCardTextTwo = (trackerStatus: BloodTestTrackingStatus | null) => {
    switch (trackerStatus) {
        case null:
        case BloodTestTrackingStatus.None:
            return "Acquista Percorso Health"
        case BloodTestTrackingStatus.Purchased:
            return "In arrivo da te!"
        case BloodTestTrackingStatus.Delivered:
            return "Effettua il test"
        case BloodTestTrackingStatus.TestDone:
            return "Registra campione"
        case BloodTestTrackingStatus.SampleRegistered:
            return "Richiedi il ritiro"
        case BloodTestTrackingStatus.PickupRequested:
            return "Modifica il ritiro"
        case BloodTestTrackingStatus.DeliveredToLab:
            return "In attesa dei risultati"
        case BloodTestTrackingStatus.ReportGenerated:
            return "Visualizza risultati"
        default:
            return ""
    }
}
